import type { RedirectRequest } from "@azure/msal-browser";
import { logger } from "../logger-init";
import { rememberReturnPath } from "./oauthReturnHandler";

/**
 * Interactive (redirect) sign-in recovery: the one place the app is allowed
 * to send the browser to B2C because a silent token request failed.
 *
 * Two invariants, both learned from the 2026-09-28/29 redirect-loop incident:
 *
 * 1. NEVER two `acquireTokenRedirect` calls in one page. msal-browser runs
 *    `resetRequestCache()` in that method's catch block, so a second call that
 *    fails with `interaction_in_progress` wipes the FIRST call's PKCE verifier
 *    and interaction flag (sessionStorage and cookie) right before the browser
 *    leaves. On return msal finds "no interaction in progress", never redeems
 *    the auth code, the cached account stays token-less, and app-init races
 *    the same two calls again — forever. Concurrent callers therefore share one
 *    in-flight redirect.
 *
 * 2. A redirect that keeps coming back without tokens must stop. After
 *    REDIRECT_LOOP_LIMIT redirects inside REDIRECT_LOOP_WINDOW_MS (tracked in
 *    sessionStorage, so it survives the round trips within the tab) the cached
 *    account is dropped and the page reloads, which puts the user on the clean
 *    `loginRedirect` path instead of another `acquireTokenRedirect`.
 */

export const REDIRECT_LOOP_LIMIT = 3;
export const REDIRECT_LOOP_WINDOW_MS = 60_000;
const HISTORY_KEY = "sky-auth-redirects";

/** The two msal methods this module needs; keeps tests free of a real PublicClientApplication. */
export interface RedirectCapable {
  acquireTokenRedirect(request: RedirectRequest): Promise<void>;
  clearCache(): Promise<void>;
}

interface Options {
  /** Full-page navigation used by the loop breaker (defaults to location.replace). */
  navigate?: (url: string) => void;
  /** Clock, for tests. */
  now?: () => number;
}

let inFlight: Promise<never> | null = null;

function readHistory(): number[] {
  try {
    const raw = window.sessionStorage.getItem(HISTORY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((t): t is number => typeof t === "number") : [];
  } catch {
    return [];
  }
}

function writeHistory(timestamps: number[]): void {
  try {
    window.sessionStorage.setItem(HISTORY_KEY, JSON.stringify(timestamps));
  } catch {
    /* storage unavailable — the loop breaker degrades to "no breaker" */
  }
}

/** Forget past redirects. Called once a token has actually been obtained. */
export function clearRedirectHistory(): void {
  try {
    window.sessionStorage.removeItem(HISTORY_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Record that this page is about to send the browser to B2C. Returns false —
 * and consumes the history, so the user's next deliberate retry is allowed —
 * when that would be redirect number REDIRECT_LOOP_LIMIT + 1 inside the window.
 * Shared by both interactive paths (`acquireTokenRedirect` recovery and the
 * no-account `loginRedirect`), so a loop that alternates between them still
 * trips.
 */
export function registerRedirectAttempt(now: number = Date.now()): boolean {
  const recent = readHistory().filter((t) => now - t < REDIRECT_LOOP_WINDOW_MS);
  if (recent.length >= REDIRECT_LOOP_LIMIT) {
    clearRedirectHistory();
    return false;
  }
  writeHistory([...recent, now]);
  return true;
}

/** The error every caller surfaces when the breaker trips; the login screen renders it. */
export function redirectLoopError(): Error & { errorCode: string } {
  return Object.assign(new Error("Sign-in keeps being redirected back to the start"), {
    errorCode: "redirect_loop_detected",
  });
}

/**
 * Start (or join) the page's single interactive redirect. The returned promise
 * never resolves: it rejects if msal refuses to redirect, with
 * `redirect_loop_detected` when the breaker trips, or with `redirecting` in
 * the unlikely case the page is still alive after the navigation timeout.
 */
export function beginInteractiveRedirect(
  instance: RedirectCapable,
  request: RedirectRequest,
  opts: Options = {},
): Promise<never> {
  if (inFlight) return inFlight;

  const now = opts.now ? opts.now() : Date.now();
  const navigate = opts.navigate ?? ((url: string) => window.location.replace(url));

  if (!registerRedirectAttempt(now)) {
    logger.error(
      "Sign-in redirect loop detected — dropping the cached account and starting a clean sign-in",
      undefined,
      { component: "interactiveRedirect", limit: REDIRECT_LOOP_LIMIT },
    );
    // The stale account is what keeps pulling app-init into acquireTokenRedirect;
    // without it the reload lands on the no-account loginRedirect path instead.
    inFlight = instance
      .clearCache()
      .catch(() => undefined)
      .then(() => {
        navigate(window.location.pathname + window.location.search);
        throw redirectLoopError();
      });
    return inFlight;
  }

  // An expired session still has a cached account, so it comes here rather
  // than to the no-account login paths that keep the page: keep it here too,
  // or a link opened from an email lands on home after signing in again.
  rememberReturnPath();

  inFlight = instance.acquireTokenRedirect(request).then(
    () => {
      // acquireTokenRedirect resolves only after msal's navigation timeout —
      // the page is normally gone long before this.
      throw Object.assign(new Error("Redirecting to login..."), { errorCode: "redirecting" });
    },
    (error: unknown) => {
      // msal refused (popup blocked, another flow in progress, …): let the
      // next caller try again.
      inFlight = null;
      throw error;
    },
  );
  return inFlight;
}

/** The sign-in page (on unless VITE_SIGN_IN_PAGE is "false"), as in index.tsx. */
const SIGN_IN_PAGE = import.meta.env.VITE_SIGN_IN_PAGE !== "false";

/**
 * What to do when a silent token request needs the person to sign in again:
 * with the sign-in page on, drop the cached account and go back to it (keeping
 * the page they were on); otherwise the B2C redirect above.
 *
 * The account sits in localStorage, so it outlives the session: a person who
 * comes back after their sign-in ran out still looks signed in, the app loads,
 * the first token refresh fails, and a redirect from here would open
 * login.skypointcloud.com instead of our sign-in page.
 */
export function recoverSignIn(
  instance: RedirectCapable,
  request: RedirectRequest,
  opts: Options & { signInPage?: boolean } = {},
): Promise<never> {
  if (!(opts.signInPage ?? SIGN_IN_PAGE)) return beginInteractiveRedirect(instance, request, opts);
  if (inFlight) return inFlight;

  const navigate = opts.navigate ?? ((url: string) => window.location.replace(url));
  rememberReturnPath();
  // No account left, so the reload shows the sign-in page; nothing goes to
  // B2C from here, so there is no round trip that could loop.
  inFlight = instance
    .clearCache()
    .catch(() => undefined)
    .then(() => {
      navigate("/");
      throw Object.assign(new Error("Signing in again..."), { errorCode: "redirecting" });
    });
  return inFlight;
}

/** Test seam: forget the in-flight redirect (and, unless asked, the history). */
export function __resetInteractiveRedirectForTests(opts: { keepHistory?: boolean } = {}): void {
  inFlight = null;
  if (!opts.keepHistory) clearRedirectHistory();
}
